import React, { useCallback, useMemo } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { type Stages, type Stage, ProgressTracker } from '@atlaskit/progress-tracker';
import { ScreenReaderText } from '@atlassian/jira-accessibility';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { StepComponent } from '../../constants/steps';
import { CloseAndReturnButton } from '../close-and-return-button';
import messages from './messages';

type StepProgressBarProps = {
	productSteps: StepComponent[];
	currentStepIndex: number;
};

export const StepProgressBar = ({ productSteps, currentStepIndex }: StepProgressBarProps) => {
	const { formatMessage } = useIntl();

	const getPercentageComplete = useCallback(
		(index: number, currStepIndex: number) => (index < currStepIndex ? 100 : 0),
		[],
	);

	const getStatus = useCallback((index: number, currStepIndex: number): Stage['status'] => {
		if (index < currStepIndex) {
			return 'visited';
		}
		if (index === currStepIndex) {
			return 'current';
		}
		return 'unvisited';
	}, []);

	const items: Stages = useMemo(
		() =>
			fg('enable-x2c-standardised-flow')
				? productSteps
						.filter((step) => step.progressBarStepLabel)
						.map(({ step: configStep, progressBarStepLabel }, index) => ({
							id: configStep,
							label: progressBarStepLabel ? formatMessage(progressBarStepLabel) : '',
							percentageComplete: getPercentageComplete(index, currentStepIndex),
							status: getStatus(index, currentStepIndex),
							noLink: true,
						}))
				: productSteps.map(({ step: configStep, progressBarStepLabel }, index) => ({
						id: configStep,
						label: progressBarStepLabel ? formatMessage(progressBarStepLabel) : '',
						percentageComplete: getPercentageComplete(index, currentStepIndex),
						status: getStatus(index, currentStepIndex),
						noLink: true,
					})),
		[currentStepIndex, formatMessage, getPercentageComplete, getStatus, productSteps],
	);

	if (!productSteps || currentStepIndex < 0) {
		return null;
	}

	let currentStepLabel = productSteps[currentStepIndex].progressBarStepLabel
		? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			formatMessage(productSteps[currentStepIndex].progressBarStepLabel!)
		: '';

	if (fg('enable-x2c-standardised-flow')) {
		currentStepLabel = items[currentStepIndex].label;
	}

	return (
		<>
			<ScreenReaderText>
				{formatMessage(messages.screenReaderText, {
					currentStepIndex: currentStepIndex + 1,
					numSteps: productSteps.length,
					currentStepLabel,
				})}
			</ScreenReaderText>
			<ProgressTracker
				items={items}
				animated={false}
				testId="project-create-importer-upload-flow.common.ui.step-progress-bar.progress-tracker"
			/>
		</>
	);
};

type ProgressBarStandardProps = StepProgressBarProps & {
	onClose: () => void;
	showCrossButton?: boolean;
	shouldShowCancelModal?: boolean;
};

export const ProgressBarStandard = ({
	onClose,
	showCrossButton = true,
	shouldShowCancelModal = true,
	...props
}: ProgressBarStandardProps) => (
	<Box
		xcss={wrapperStyles}
		testId="project-create-importer-upload-flow.common.ui.step-progress-bar.standard"
	>
		{showCrossButton && (
			<Box xcss={crossStyles}>
				<CloseAndReturnButton
					iconOnly
					iconSize="medium"
					onCloseAndReturnToUploadStep={onClose}
					shouldShowCancelModal={shouldShowCancelModal}
				/>
			</Box>
		)}
		<StepProgressBar {...props} />
	</Box>
);

const wrapperStyles = xcss({
	display: 'flex',
	position: 'fixed',
	height: '120px',
	width: '100%',
	top: 'space.0',
	left: 'space.0',
	borderBottomStyle: 'solid',
	borderBottomWidth: 'space.025',
	borderBottomColor: 'color.border',
	backgroundColor: 'color.background.input',
	zIndex: 'navigation',
});

const crossStyles = xcss({
	position: 'fixed',
	top: 'space.0',
	left: 'space.0',
	paddingInline: 'space.300',
	paddingBlock: 'space.400',
});
